import type { FramingBox, FramingSceneInspector } from "../framingInspector";
import { cropWindowAtTime, observationsAtTime } from "../framingOverlayMath";

/**
 * Task-oriented overlay presets and fail-closed geometry validation
 * (CROP-WB-009). Presets are data; every projected shape carries a number,
 * a pattern class, and provenance so meaning never depends on color alone.
 */

export type FramingOverlayClass =
  | "target"
  | "observation"
  | "safety_region"
  | "delivered_subject"
  | "planned_crop"
  | "applied_crop"
  | "provider_safe_zone"
  | "effect_viewport"
  | "tracking_path"
  | "visible_content"
  | "unrecognized";

export type FramingOverlayPresetKey =
  | "subject_safety"
  | "plan_vs_applied"
  | "transform_lineage"
  | "effects"
  | "none"
  | "advanced";

export const FRAMING_OVERLAY_PRESETS: Record<
  FramingOverlayPresetKey,
  readonly FramingOverlayClass[]
> = {
  subject_safety: [
    "target",
    "observation",
    "safety_region",
    "delivered_subject",
  ],
  plan_vs_applied: ["planned_crop", "applied_crop"],
  transform_lineage: ["visible_content", "provider_safe_zone", "applied_crop"],
  effects: ["effect_viewport", "tracking_path", "applied_crop"],
  none: [],
  advanced: [
    "target",
    "observation",
    "safety_region",
    "delivered_subject",
    "planned_crop",
    "applied_crop",
    "provider_safe_zone",
    "effect_viewport",
    "tracking_path",
    "visible_content",
    "unrecognized",
  ],
};

export const FRAMING_OVERLAY_CLASS_LABELS: Record<FramingOverlayClass, string> =
  {
    target: "Intended target",
    observation: "Detector observation",
    safety_region: "Protected region",
    delivered_subject: "Delivered subject evidence",
    planned_crop: "Planned crop window",
    applied_crop: "Applied crop (receipt)",
    provider_safe_zone: "Provider / delivery safe zone",
    effect_viewport: "Effect viewport",
    tracking_path: "Tracking crop at time",
    visible_content: "Visible content (non-padding)",
    unrecognized: "Unrecognized evidence",
  };

/** SVG dash patterns per class: meaning survives without color. */
export const FRAMING_OVERLAY_CLASS_PATTERNS: Record<
  FramingOverlayClass,
  string
> = {
  target: "0",
  observation: "6 3",
  safety_region: "2 3",
  delivered_subject: "10 4",
  planned_crop: "12 3 3 3",
  applied_crop: "0",
  provider_safe_zone: "4 6",
  effect_viewport: "8 2 2 2",
  tracking_path: "3 3",
  visible_content: "1 4",
  unrecognized: "2 8",
};

export type FramingOverlayShape = {
  /** Stable per-pane number shown on the outline and in the table. */
  index: number;
  overlayClass: FramingOverlayClass;
  label: string;
  box: FramingBox;
  coordinateSpaceId: string;
  provenance: "exact" | "inferred" | "interpolated";
  subjectId: string | null;
  detail: string | null;
};

export type FramingOverlayWarning = {
  overlayClass: FramingOverlayClass;
  reason: string;
};

const NORMALIZED_TOLERANCE = 0.02;

/**
 * Fail-closed geometry validation: NaN/Infinity, inverted or empty boxes,
 * and values far outside the normalized range are rejected; slight
 * tolerance overshoot is clamped.
 */
export function validateOverlayBox(box: FramingBox): FramingBox | null {
  const values = [box.xMin, box.yMin, box.xMax, box.yMax];
  if (
    values.some((value) => typeof value !== "number" || !Number.isFinite(value))
  ) {
    return null;
  }
  if (box.xMax <= box.xMin || box.yMax <= box.yMin) return null;
  if (
    values.some(
      (value) =>
        value < -NORMALIZED_TOLERANCE || value > 1 + NORMALIZED_TOLERANCE,
    )
  ) {
    return null;
  }
  const clamp = (value: number) => Math.min(1, Math.max(0, value));
  return {
    xMin: clamp(box.xMin),
    yMin: clamp(box.yMin),
    xMax: clamp(box.xMax),
    yMax: clamp(box.yMax),
  };
}

type ShapeCandidate = Omit<FramingOverlayShape, "index" | "box"> & {
  box: FramingBox | null | undefined;
};

/**
 * Build the overlay shapes for one pane, restricted to geometry declared in
 * that pane's coordinate space. Invalid shapes are omitted with a visible
 * warning; they never crash the pane or hide the media (invariant 8).
 */
export function buildOverlayShapes(
  inspector: FramingSceneInspector,
  paneSpaceId: string,
  preset: FramingOverlayPresetKey,
  timeSeconds: number,
  enabledClasses?: readonly FramingOverlayClass[],
): { shapes: FramingOverlayShape[]; warnings: FramingOverlayWarning[] } {
  const active = new Set(enabledClasses ?? FRAMING_OVERLAY_PRESETS[preset]);
  const candidates: ShapeCandidate[] = [];
  const decision = inspector.decision;
  const receipt = inspector.appliedReceipt;

  if (active.has("observation")) {
    const visible = observationsAtTime(
      inspector.observations.filter(
        (observation) => observation.coordinateSpaceId === paneSpaceId,
      ),
      timeSeconds,
    );
    for (const observation of visible) {
      candidates.push({
        overlayClass: "observation",
        label: `${observation.subjectId ?? observation.kind ?? "subject"}`,
        box: observation.bbox,
        coordinateSpaceId: paneSpaceId,
        provenance: "exact",
        subjectId: observation.subjectId ?? null,
        detail: [
          observation.detector,
          observation.confidence != null
            ? `conf ${observation.confidence.toFixed(2)}`
            : null,
        ]
          .filter(Boolean)
          .join(" · "),
      });
    }
  }
  if (decision && decision.coordinateSpaceId === paneSpaceId) {
    if (active.has("safety_region") && decision.protectedRegion) {
      candidates.push({
        overlayClass: "safety_region",
        label: "Protected region",
        box: decision.protectedRegion,
        coordinateSpaceId: paneSpaceId,
        provenance: "exact",
        subjectId: null,
        detail: decision.protectedSubjectIds.join(", ") || null,
      });
    }
    if (active.has("planned_crop")) {
      const plannedWindow =
        decision.cropPath.length > 0
          ? cropWindowAtTime(decision.cropPath, timeSeconds)
          : decision.cropWindow;
      if (plannedWindow) {
        candidates.push({
          overlayClass: "planned_crop",
          label: "Planned crop",
          box: plannedWindow,
          coordinateSpaceId: paneSpaceId,
          provenance: decision.cropPath.length > 0 ? "interpolated" : "exact",
          subjectId: null,
          detail: decision.strategy ?? null,
        });
      }
    }
    if (active.has("visible_content") && decision.visibleContentBox) {
      candidates.push({
        overlayClass: "visible_content",
        label: "Visible content",
        box: decision.visibleContentBox,
        coordinateSpaceId: paneSpaceId,
        provenance: "exact",
        subjectId: null,
        detail: "excludes contain padding",
      });
    }
  }
  if (active.has("provider_safe_zone") && decision) {
    for (const zone of decision.safeZones) {
      if (
        (zone.coordinateSpaceId ?? decision.coordinateSpaceId) !== paneSpaceId
      )
        continue;
      candidates.push({
        overlayClass: "provider_safe_zone",
        label: `Safe zone (${zone.kind})`,
        box: zone.box,
        coordinateSpaceId: paneSpaceId,
        provenance: "exact",
        subjectId: null,
        detail: null,
      });
    }
  }
  if (receipt) {
    const receiptSpace = receipt.sourceCoordinateSpaceId ?? null;
    if (
      active.has("applied_crop") &&
      (receiptSpace === paneSpaceId || receiptSpace == null)
    ) {
      const appliedWindow =
        receipt.cropPath.length > 0
          ? cropWindowAtTime(receipt.cropPath, timeSeconds)
          : receipt.cropWindow;
      if (appliedWindow) {
        candidates.push({
          overlayClass: "applied_crop",
          label: "Applied crop",
          box: appliedWindow,
          coordinateSpaceId: paneSpaceId,
          provenance:
            receipt.cropPath.length > 0
              ? "interpolated"
              : receiptSpace == null
                ? "inferred"
                : "exact",
          subjectId: null,
          detail: receipt.renderer ?? null,
        });
      }
    }
    if (
      active.has("effect_viewport") &&
      receipt.effectViewport &&
      (receipt.effectViewport.coordinateSpaceId ?? paneSpaceId) === paneSpaceId
    ) {
      const viewport =
        receipt.effectViewport.cropPath.length > 0
          ? cropWindowAtTime(receipt.effectViewport.cropPath, timeSeconds)
          : receipt.effectViewport.cropWindow;
      if (viewport) {
        candidates.push({
          overlayClass: "effect_viewport",
          label: `Effect viewport${
            receipt.effectViewport.effectType
              ? ` (${receipt.effectViewport.effectType})`
              : ""
          }`,
          box: viewport,
          coordinateSpaceId: paneSpaceId,
          provenance:
            receipt.effectViewport.cropPath.length > 0
              ? "interpolated"
              : "exact",
          subjectId: null,
          detail: null,
        });
      }
    }
  }
  if (active.has("delivered_subject")) {
    for (const qc of inspector.qcHistory) {
      for (const evidence of qc.deliveredSubjectEvidence) {
        if ((evidence.coordinateSpaceId ?? "") !== paneSpaceId) continue;
        candidates.push({
          overlayClass: "delivered_subject",
          label: `${evidence.subjectId ?? evidence.kind ?? "subject"} delivered`,
          box: evidence.bbox,
          coordinateSpaceId: paneSpaceId,
          provenance: "exact",
          subjectId: evidence.subjectId ?? null,
          detail: evidence.matchStatus ?? null,
        });
      }
    }
  }

  const shapes: FramingOverlayShape[] = [];
  const warnings: FramingOverlayWarning[] = [];
  for (const candidate of candidates) {
    if (!candidate.box) continue;
    const box = validateOverlayBox(candidate.box);
    if (box === null) {
      warnings.push({
        overlayClass: candidate.overlayClass,
        reason: `${FRAMING_OVERLAY_CLASS_LABELS[candidate.overlayClass]} has invalid geometry and was omitted.`,
      });
      continue;
    }
    shapes.push({ ...candidate, box, index: shapes.length + 1 });
  }
  return { shapes, warnings };
}
